import { instancePath } from "./instance";
/**
 * Project data store: one bootstrap per project, then deltas over WebSocket.
 * Everything a project screen renders comes from here, so filter/group changes
 * never hit the server. Persisted to IndexedDB so a reload is a delta, not a full load.
 */
import { create } from "zustand";
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import type { Schemas } from "@tern/api-client";
import { unwrap } from "@tern/api-client";
import { api, queryClient } from "./api";

export type Issue = Schemas["Issue"];
export type WorkflowState = Schemas["WorkflowState"];
export type Label = Schemas["Label"];
export type ProjectMember = Schemas["ProjectMember"];
export type ProjectWithRole = Schemas["ProjectWithRole"];
export type UserCard = Schemas["UserCard"];
export type IssueRelation = Schemas["IssueRelation"];
export type Bootstrap = Schemas["Bootstrap"];
export type Task = Schemas["Task"];
export type TaskDependency = Schemas["TaskDependency"];
export type TaskUpdate = Schemas["TaskUpdate"];
export type Milestone = Schemas["Milestone"];

export type ProjectData = {
  project: ProjectWithRole;
  states: Record<string, WorkflowState>;
  labels: Record<string, Label>;
  members: ProjectMember[];
  users: Record<string, UserCard>;
  issues: Record<string, Issue>;
  relations: Record<string, IssueRelation>;
  tasks: Record<string, Task>;
  dependencies: Record<string, TaskDependency>;
  /** Latest health report per task id. */
  health: Record<string, TaskUpdate>;
  milestones: Record<string, Milestone>;
  version: number;
  slug: string;
};

type Status = "idle" | "loading" | "ready" | "error";

type State = {
  byId: Record<string, ProjectData>;
  idByKey: Record<string, string>; // `${slug}/${KEY}` → project id
  status: Record<string, Status>;
  error: Record<string, string | undefined>;
  load: (slug: string, key: string, opts?: { force?: boolean }) => Promise<ProjectData | undefined>;
  refreshDelta: (projectId: string) => Promise<void>;
  applyIssue: (issue: Issue) => void;
  applyState: (s: WorkflowState) => void;
  applyLabel: (l: Label) => void;
  applyMembers: (projectId: string, m: ProjectMember[]) => void;
  applyRelation: (r: IssueRelation, action: "created" | "deleted") => void;
  applyProject: (p: Schemas["Project"]) => void;
  applyTask: (t: Task) => void;
  applyDependency: (d: TaskDependency, action: "created" | "deleted") => void;
  applyHealth: (u: TaskUpdate, action: "created" | "deleted") => void;
  applyMilestone: (m: Milestone) => void;
  drop: (projectId: string) => void;
};

const byIdMap = <T extends { id: string }>(rows: T[]) => Object.fromEntries(rows.map((r) => [r.id, r]));

function merge(data: ProjectData, b: Bootstrap): ProjectData {
  const next: ProjectData = b.full
    ? {
        project: b.project,
        states: byIdMap(b.states),
        labels: byIdMap(b.labels),
        members: b.members,
        users: byIdMap(b.users),
        issues: byIdMap(b.issues),
        relations: byIdMap(b.relations),
        tasks: byIdMap(b.tasks),
        dependencies: byIdMap(b.task_dependencies),
        health: Object.fromEntries(b.task_health.map((u) => [u.task_id, u])),
        milestones: byIdMap(b.milestones),
        version: b.version,
        slug: data.slug,
      }
    : {
        ...data,
        project: b.project,
        states: { ...data.states, ...byIdMap(b.states) },
        labels: { ...data.labels, ...byIdMap(b.labels) },
        members: b.members,
        users: { ...data.users, ...byIdMap(b.users) },
        issues: { ...data.issues, ...byIdMap(b.issues) },
        relations: byIdMap(b.relations),
        tasks: { ...data.tasks, ...byIdMap(b.tasks) },
        dependencies: byIdMap(b.task_dependencies),
        health: Object.fromEntries(b.task_health.map((u) => [u.task_id, u])),
        milestones: { ...data.milestones, ...byIdMap(b.milestones) },
        version: Math.max(data.version, b.version),
      };
  return next;
}

const cacheKey = (projectId: string) => `tern.${instancePath}.project.v2.${projectId}`;
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
function persist(data: ProjectData) {
  clearTimeout(saveTimers.get(data.project.id));
  saveTimers.set(
    data.project.id,
    setTimeout(() => {
      idbSet(cacheKey(data.project.id), data).catch(() => {});
    }, 500),
  );
}

export const useProjectStore = create<State>((set, get) => {
  const put = (data: ProjectData) => {
    set((s) => ({ byId: { ...s.byId, [data.project.id]: data } }));
    persist(data);
  };
  const withProject = (projectId: string, fn: (d: ProjectData) => ProjectData) => {
    const d = get().byId[projectId];
    if (d) put(fn(d));
  };

  return {
    byId: {},
    idByKey: {},
    status: {},
    error: {},

    async load(slug, key, opts) {
      const k = `${slug}/${key}`;
      const knownId = get().idByKey[k];
      if (knownId && get().status[knownId] === "ready" && !opts?.force) return get().byId[knownId];
      set((s) => ({ status: { ...s.status, [k]: "loading" } }));
      try {
        // Hydrate from IndexedDB first (if we know the id), then fetch only the delta.
        let cached: ProjectData | undefined;
        const cachedId = knownId ?? (await idbGet<string>(`tern.${instancePath}.projectId.${k}`).catch(() => undefined));
        if (cachedId) cached = await idbGet<ProjectData>(cacheKey(cachedId)).catch(() => undefined);
        const since = cached?.version ?? 0;
        const b = unwrap(
          await api.GET("/api/workspaces/{slug}/projects/{key}/bootstrap", {
            params: { path: { slug, key }, query: { since } },
          }),
        );
        const base: ProjectData = cached ?? {
          project: b.project,
          states: {},
          labels: {},
          members: [],
          users: {},
          issues: {},
          relations: {},
          tasks: {},
          dependencies: {},
          health: {},
          milestones: {},
          version: 0,
          slug,
        };
        const data = merge(base, b);
        set((s) => ({
          idByKey: { ...s.idByKey, [k]: data.project.id, [`${slug}/${data.project.key}`]: data.project.id },
          status: { ...s.status, [k]: "ready", [data.project.id]: "ready" },
          error: { ...s.error, [k]: undefined },
        }));
        put(data);
        idbSet(`tern.${instancePath}.projectId.${k}`, data.project.id).catch(() => {});
        return data;
      } catch (e) {
        set((s) => ({ status: { ...s.status, [k]: "error" }, error: { ...s.error, [k]: (e as Error).message } }));
        return undefined;
      }
    },

    async refreshDelta(projectId) {
      const d = get().byId[projectId];
      if (!d) return;
      await get().load(d.slug, d.project.key, { force: true });
    },

    applyIssue(issue) {
      withProject(issue.project_id, (d) => {
        const cur = d.issues[issue.id];
        if (cur && cur.version > issue.version) return d; // stale event
        return { ...d, issues: { ...d.issues, [issue.id]: issue }, version: Math.max(d.version, issue.version) };
      });
    },
    applyState(st) {
      withProject(st.project_id, (d) => ({ ...d, states: { ...d.states, [st.id]: st }, version: Math.max(d.version, st.version) }));
    },
    applyLabel(l) {
      withProject(l.project_id, (d) => ({ ...d, labels: { ...d.labels, [l.id]: l }, version: Math.max(d.version, l.version) }));
    },
    applyMembers(projectId, members) {
      withProject(projectId, (d) => ({ ...d, members }));
    },
    applyRelation(r, action) {
      withProject(r.project_id, (d) => {
        const relations = { ...d.relations };
        if (action === "deleted") delete relations[r.id];
        else relations[r.id] = r;
        return { ...d, relations };
      });
    },
    applyProject(p) {
      withProject(p.id, (d) => ({ ...d, project: { ...d.project, ...p } }));
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    applyTask(t) {
      withProject(t.project_id, (d) => {
        const cur = d.tasks[t.id];
        if (cur && cur.version > t.version) return d;
        return { ...d, tasks: { ...d.tasks, [t.id]: t }, version: Math.max(d.version, t.version) };
      });
    },
    applyDependency(dep, action) {
      withProject(dep.project_id, (d) => {
        const dependencies = { ...d.dependencies };
        if (action === "deleted") delete dependencies[dep.id];
        else dependencies[dep.id] = dep;
        return { ...d, dependencies };
      });
    },
    applyHealth(u, action) {
      // Only the latest report per task is kept; find the project through the task.
      const projectId = Object.values(get().byId).find((d) => d.tasks[u.task_id])?.project.id;
      if (!projectId) return;
      withProject(projectId, (d) => {
        const health = { ...d.health };
        if (action === "deleted") {
          if (health[u.task_id]?.id === u.id) delete health[u.task_id];
        } else if (!health[u.task_id] || health[u.task_id]!.created_at <= u.created_at) health[u.task_id] = u;
        return { ...d, health };
      });
      void queryClient.invalidateQueries({ queryKey: ["task", u.task_id, "updates"] });
    },
    applyMilestone(m) {
      withProject(m.project_id, (d) => {
        const cur = d.milestones[m.id];
        if (cur && cur.version > m.version) return d;
        return { ...d, milestones: { ...d.milestones, [m.id]: m }, version: Math.max(d.version, m.version) };
      });
    },
    drop(projectId) {
      set((s) => {
        const byId = { ...s.byId };
        delete byId[projectId];
        return { byId };
      });
      idbDel(cacheKey(projectId)).catch(() => {});
    },
  };
});

// ---- Selectors -------------------------------------------------------------

export const useProject = (slug: string, key: string) => {
  const id = useProjectStore((s) => s.idByKey[`${slug}/${key}`]);
  return useProjectStore((s) => (id ? s.byId[id] : undefined));
};

export const liveIssues = (d: ProjectData) => Object.values(d.issues).filter((i) => !i.deleted_at && !i.archived_at);
export const liveStates = (d: ProjectData) => Object.values(d.states).filter((s) => !s.deleted_at).sort((a, b) => a.position - b.position);
export const liveLabels = (d: ProjectData) => Object.values(d.labels).filter((l) => !l.deleted_at).sort((a, b) => a.name.localeCompare(b.name));
export const identifier = (d: ProjectData, i: Issue) => `${d.project.key}-${i.number}`;
export const liveTasks = (d: ProjectData) => Object.values(d.tasks).filter((t) => !t.deleted_at && !t.archived_at);
export const liveMilestones = (d: ProjectData) => Object.values(d.milestones).filter((m) => !m.deleted_at).sort((a, b) => (a.target_date < b.target_date ? -1 : a.target_date > b.target_date ? 1 : a.sort_order < b.sort_order ? -1 : 1));

// ---- Optimistic issue mutations --------------------------------------------

export type IssuePatch = Schemas["UpdateIssue"];

/** Apply a patch locally, send it, and roll back on failure. */
export async function patchIssue(issue: Issue, patch: IssuePatch): Promise<Issue> {
  const store = useProjectStore.getState();
  const optimistic: Issue = { ...issue };
  if (patch.title != null) optimistic.title = patch.title;
  if (patch.description != null) optimistic.description = patch.description;
  if (patch.state_id != null) optimistic.state_id = patch.state_id;
  if (patch.priority != null) optimistic.priority = patch.priority;
  if (patch.label_ids != null) optimistic.label_ids = patch.label_ids;
  if (patch.assignee_id !== undefined) optimistic.assignee_id = patch.assignee_id;
  if (patch.estimate !== undefined) optimistic.estimate = patch.estimate;
  if (patch.due_date !== undefined) optimistic.due_date = patch.due_date;
  if (patch.parent_id !== undefined) optimistic.parent_id = patch.parent_id;
  if (patch.task_id !== undefined) optimistic.task_id = patch.task_id;
  store.applyIssue(optimistic);
  try {
    const saved = unwrap(await api.PATCH("/api/issues/{id}", { params: { path: { id: issue.id } }, body: patch }));
    store.applyIssue(saved);
    return saved;
  } catch (e) {
    // Roll back regardless of version comparison.
    useProjectStore.setState((s) => {
      const d = s.byId[issue.project_id];
      if (!d) return s;
      return { byId: { ...s.byId, [issue.project_id]: { ...d, issues: { ...d.issues, [issue.id]: issue } } } };
    });
    throw e;
  }
}

export async function deleteIssue(issue: Issue) {
  const store = useProjectStore.getState();
  store.applyIssue({ ...issue, deleted_at: new Date().toISOString() });
  try {
    unwrap(await api.DELETE("/api/issues/{id}", { params: { path: { id: issue.id } } }));
  } catch (e) {
    store.applyIssue(issue);
    throw e;
  }
}

export async function restoreIssue(issue: Issue) {
  const saved = unwrap(await api.POST("/api/issues/{id}/restore", { params: { path: { id: issue.id } } }));
  useProjectStore.getState().applyIssue(saved);
  return saved;
}

// ---- Optimistic task mutations ----------------------------------------------

export type TaskPatch = Schemas["UpdateTask"];

export async function patchTask(task: Task, patch: TaskPatch): Promise<Task> {
  const store = useProjectStore.getState();
  const optimistic: Task = { ...task };
  if (patch.name != null) optimistic.name = patch.name;
  if (patch.summary != null) optimistic.summary = patch.summary;
  if (patch.description != null) optimistic.description = patch.description;
  if (patch.status != null) optimistic.status = patch.status;
  if (patch.priority != null) optimistic.priority = patch.priority;
  if (patch.lead_id !== undefined) optimistic.lead_id = patch.lead_id;
  if (patch.start_date !== undefined) optimistic.start_date = patch.start_date;
  if (patch.target_date !== undefined) optimistic.target_date = patch.target_date;
  if (patch.milestone_id !== undefined) optimistic.milestone_id = patch.milestone_id;
  if (patch.parent_id !== undefined) optimistic.parent_id = patch.parent_id;
  if (patch.member_ids != null) optimistic.member_ids = patch.member_ids;
  if (patch.label_ids != null) optimistic.label_ids = patch.label_ids;
  store.applyTask(optimistic);
  try {
    const saved = unwrap(await api.PATCH("/api/tasks/{id}", { params: { path: { id: task.id } }, body: patch }));
    store.applyTask(saved);
    return saved;
  } catch (e) {
    useProjectStore.setState((s) => {
      const d = s.byId[task.project_id];
      if (!d) return s;
      return { byId: { ...s.byId, [task.project_id]: { ...d, tasks: { ...d.tasks, [task.id]: task } } } };
    });
    throw e;
  }
}

export async function deleteTask(task: Task) {
  unwrap(await api.DELETE("/api/tasks/{id}", { params: { path: { id: task.id } } }));
  useProjectStore.getState().applyTask({ ...task, deleted_at: new Date().toISOString() });
  // Children and issues were re-parented server-side; pull the delta.
  await useProjectStore.getState().refreshDelta(task.project_id);
}
